import { action, set } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { TwoFactorDetails } from '@square/dashboard-protos/models/squareup/multipass/common';
import { v4 as uuid } from 'uuid';
import Controller from '@ember/controller';
import focusInput from '@banking/savings-onboarding-app/utils/focus-input';
import MultipassExternalApi from '@banking/api/services/multipass-external-api';
import type IntlService from 'ember-intl/services/intl';
import { Error as MPError } from '@square/dashboard-protos/models/squareup/multipass/external';
import type RouterService from '@ember/routing/router-service';

const INPUT_SELECTOR = '#sfs-signup__phone-number';

// optional prefix of 1, followed by 2-9, then any 9 digits
const US_PHONE_REGEX = /^1?[2-9]\d{9}$/;

const BAD_NUMBER_ERRORS = new Set([
  MPError.INVALID_ALIAS,
  MPError.BLOCKED_SMS_NUMBER,
  MPError.TEMPORARY_LOCKOUT,
]);

export default class TwoFactorEnrollController extends Controller {
  @service intl!: IntlService;
  @service multipassExternalApi!: MultipassExternalApi;
  @service router!: RouterService;

  @tracked isLoading = false;
  @tracked showValidation = false;
  @tracked serverError = false;
  @tracked phoneNumber = '';

  get clientInvalid() {
    return !this.phoneNumber || !US_PHONE_REGEX.test(this.phoneNumber);
  }

  get validation() {
    return {
      isInvalid: this.clientInvalid || this.serverError,
      message: this.intl.t('sfs.signup.twoFactor.enroll.phoneNumberValidation'),
    };
  }

  @action async submit(event: Event) {
    event.preventDefault();

    if (this.clientInvalid) {
      this.showValidation = true;
      focusInput(INPUT_SELECTOR);
    } else {
      this.showValidation = false;
      this.isLoading = true;

      // E164 formatted phone number (with + and country code)
      // TODO: set formatted phone number in the twoFactorDetails description
      try {
        const input = <HTMLInputElement>document.querySelector(INPUT_SELECTOR);
        const smsTwoFactorDetails = {
          description: input.value,
          sms: {
            phone: this.canonicalPhone(),
          },
        } as TwoFactorDetails;

        const response = await this.multipassExternalApi.enrollTwoFactor({
          twoFactorDetails: smsTwoFactorDetails,
          idempotenceKey: uuid(),
        });
        if (response.error === MPError.NO_ERROR) {
          set(this.model, 'smsTwoFactor', smsTwoFactorDetails);
          this.router.transitionTo('signup.two-factor.sms');
        } else if (BAD_NUMBER_ERRORS.has(response.error)) {
          this.serverError = true;
          this.showValidation = true;
          focusInput(INPUT_SELECTOR);
        } else {
          throw new Error(MPError[response.error]);
        }
      } catch (error) {
        this.send('handleRpcError', error);
      } finally {
        this.isLoading = false;
      }
    }
  }

  // Copied from dashboard/lib/account-settings/app/components/accounts/two-factor/add-method-sms-modal.js
  canonicalPhone() {
    const raw = this.phoneNumber || '';
    let digits = raw.replace(/[^\d+]/g, '');
    if (!/^\+/.test(digits)) {
      digits = `+1${digits}`;
    }
    return digits;
  }
}
